import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'react-toastify'

const API_URL = import.meta.env.VITE_API_URL || ''

// --- Flag de logout para suprimir errores durante cierre de sesión ---
let isLoggingOut = false

export function setLoggingOut(value: boolean): void {
  isLoggingOut = value
}

// Crear instancia de Axios
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request Interceptor - Agregar token a todas las requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token')

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Identifica el canal para la política de sesión por canal.
    if (config.headers && !config.headers['x-client-type']) {
      config.headers['x-client-type'] = 'web'
    }

    // ✅ NO forzar Content-Type cuando es FormData (Axios lo maneja automáticamente con el boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      // Las subidas de archivos (videos/PDF/audio) pueden tardar más que el timeout
      // global de 30s. Un video de ~38MB en una conexión típica supera los 30s y axios
      // abortaba la petición antes de completar (síntoma: archivos grandes "no avanzan",
      // los pequeños sí). Damos 5 min a los uploads multipart salvo override explícito.
      if (config.timeout == null || config.timeout === 30000) {
        config.timeout = 300000
      }
    }

    // Log en desarrollo
    if (import.meta.env.VITE_DEBUG === 'true') {
      // console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
      //         headers: config.headers,
      //         data: config.data,
      //       })
      void 0;
    }

    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Variables para manejar el refresh token
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Response Interceptor - Manejar errores globalmente
api.interceptors.response.use(
  (response) => {
    // Log en desarrollo
    if (import.meta.env.VITE_DEBUG === 'true') {
      // console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      //         status: response.status,
      //         data: response.data,
      //       })
      void 0;
    }

    return response
  },
  async (error: AxiosError) => {
    // Suprimir TODOS los errores durante logout (race condition prevention)
    if (isLoggingOut) {
      return Promise.reject(error)
    }

    // Suprimir requests canceladas (AbortController)
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Suprimir logs para rutas de auth donde los errores son esperados (401 validate, 409 login, etc.)
    const silentAuthUrls = ['/api/auth/validate', '/api/auth/login', '/api/auth/refresh_token']
    const isSilentRoute = silentAuthUrls.some(url => originalRequest?.url?.includes(url))

    if (!isSilentRoute) {
      console.error('[API Response Error]', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
      })
    }

    // Si es 401 (No autorizado) y no es la ruta de login/refresh
    const isAuthRoute = originalRequest?.url === '/api/auth/login' ||
      originalRequest?.url === '/api/auth/refresh_token'

    if (error.response?.status === 401 && !isAuthRoute) {
      // No intentar refresh durante logout
      if (isLoggingOut) {
        return Promise.reject(error)
      }

      // Si el backend indica que la sesión fue revocada (login en otro
      // dispositivo o logout forzado), no tiene sentido refrescar:
      // limpiamos local y mandamos a login con mensaje específico.
      const errCode = (error.response?.data as any)?.error
      if (errCode === 'session_revoked') {
        processQueue(error, null)
        isRefreshing = false
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('sid')
        try {
          toast.info('Tu sesión fue iniciada en otro dispositivo.')
        } catch {/* noop */}
        window.location.href = '/login?reason=session_revoked'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Si ya hay un refresh en progreso, agregar esta request a la cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return api(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true

      // ── Coordinacion ENTRE PESTANAS ─────────────────────────────────────
      // `isRefreshing` solo serializa dentro de esta pestana. Con varias
      // abiertas, cada una renovaba por su cuenta: la primera rotaba el token y
      // la segunda lo intentaba con el ya caducado, el backend lo tomaba por
      // robo y revocaba la sesion -> todas las pestanas afuera.
      // Si otra pestana ya renovo (localStorage tiene un token distinto al que
      // acaba de fallar), se reintenta con ese y no se renueva nada.
      const tokenUsado = String(originalRequest.headers?.Authorization || '').replace('Bearer ', '')
      const tokenActual = localStorage.getItem('token')
      if (tokenActual && tokenUsado && tokenActual !== tokenUsado) {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${tokenActual}`
        }
        return api(originalRequest)
      }

      isRefreshing = true

      // Intentar refrescar el token (web: cookie HTTPOnly jrt)
      try {
        // console.log('[API] Attempting token refresh...')
        void 0;

        const response = await axios.post(
          `${API_URL}/api/auth/refresh_token`,
          {},
          {
            baseURL: API_URL,
            withCredentials: true,
            headers: { 'x-client-type': 'web' }
          }
        )

        const { token: newToken } = response.data

        // console.log('[API] Token refresh successful')
        void 0;

        // Guardar nuevo token
        localStorage.setItem('token', newToken)

        // Procesar la cola de requests pendientes
        processQueue(null, newToken)

        // Reintentar request original con nuevo token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }

        isRefreshing = false
        return api(originalRequest)
      } catch (refreshError: any) {
        const refreshErrCodeRaw = refreshError?.response?.data?.error
        // Un refresh fallido NO es necesariamente una sesion muerta: puede ser un
        // 502 durante un reinicio de node-1/node-2, un corte de red o un fallo
        // puntual. Antes cualquiera de esos casos expulsaba al usuario en el acto
        // y sin reintentar, que es lo que produce los relogueos cada 15 min.
        // Solo la revocacion explicita es definitiva; el resto merece un reintento.
        if (
          refreshErrCodeRaw !== 'session_revoked' &&
          !(originalRequest as any)._reintentoRefresh
        ) {
          ;(originalRequest as any)._reintentoRefresh = true
          processQueue(null, localStorage.getItem('token'))
          isRefreshing = false
          await new Promise(resolve => setTimeout(resolve, 1500))
          return api(originalRequest)
        }

        // Si falla el refresh, limpiar tokens y redirigir a login
        console.error('[API] Token refresh failed:', refreshError)

        processQueue(refreshError, null)
        isRefreshing = false

        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('sid')

        const refreshErrCode = refreshError?.response?.data?.error
        if (refreshErrCode === 'session_revoked') {
          try { toast.info('Tu sesión fue iniciada en otro dispositivo.') } catch {/* noop */}
          window.location.href = '/login?reason=session_revoked'
        } else {
          toast.error('Sesión expirada. Por favor inicia sesión nuevamente.')
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    // Manejar otros errores
    // Mostrar toast para errores específicos
    if (error.response?.status === 403) {
      toast.error('No tienes permisos para realizar esta acción')
    } else if (error.response?.status === 404) {
      toast.error('Recurso no encontrado')
    } else if (error.response?.status === 429) {
      toast.error('Demasiadas solicitudes. Por favor, espera un momento.')
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error('Error del servidor. Por favor, intenta más tarde.')
    } else if (!error.response) {
      // No mostrar si no hay token (usuario cerró sesión)
      if (localStorage.getItem('token')) {
        toast.error('Error de conexión. Verifica tu conexión a internet.')
      }
    }

    return Promise.reject(error)
  }
)

// ── Refresco proactivo ──────────────────────────────────────────────────────
// El access token dura 15 min y hasta ahora solo se renovaba de forma reactiva,
// al recibir un 401. Es decir, toda la sesion pendia de UNA peticion cada 15 min:
// si esa coincidia con un 502 por reinicio de un nodo o con un corte de red, el
// usuario acababa en /login. Renovando a los 12 min (y al volver a la pestana)
// hay varias oportunidades antes de que el token expire de verdad.
const REFRESCO_MS = 12 * 60 * 1000
let temporizadorRefresco: number | undefined

async function refrescarProactivo(): Promise<void> {
  if (!localStorage.getItem('token') || isLoggingOut || isRefreshing) return
  try {
    isRefreshing = true
    const respuesta = await axios.post(
      `${API_URL}/api/auth/refresh_token`,
      {},
      {
        baseURL: API_URL,
        withCredentials: true,
        headers: { 'x-client-type': 'web' }
      }
    )
    if (respuesta?.data?.token) {
      localStorage.setItem('token', respuesta.data.token)
    }
  } catch {
    // Silencioso a proposito: el interceptor de 401 sigue siendo la red de
    // seguridad. Un fallo aqui no debe molestar ni expulsar al usuario.
  } finally {
    isRefreshing = false
  }
}

export function iniciarRefrescoProactivo(): void {
  if (temporizadorRefresco) return
  temporizadorRefresco = window.setInterval(refrescarProactivo, REFRESCO_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refrescarProactivo()
  })
}

export default api
